import { PlateRole } from '@parsleyai/ui'
import { imageStateOf, readyRenditions } from '@/lib/mealImages'
import { getCookTimeMinutes, type StoredMeal } from '@/types/mealPlan'
import type { LibraryMeal, MealCookActivity, MealUserState } from './types'

// The plate roles a meal's components fill, in the fixed protein → starch →
// veggie → sauce display order the role dots render in.
function rolesOf(meal: StoredMeal): PlateRole[] {
  const roles: PlateRole[] = []
  if (meal.proteins.length > 0) roles.push(PlateRole.Protein)
  if (meal.starches.length > 0) roles.push(PlateRole.Starch)
  if (meal.veggies.length > 0) roles.push(PlateRole.Veggie)
  if (meal.sauce) roles.push(PlateRole.Sauce)
  return roles
}

// Pure join: canonical meal + the user's state + derived activity → a render-ready
// LibraryMeal. The single place the library reads the persistence shapes, so a
// storage change lands here alone (mirrors meals/mappers.ts). Kept React/i18n-free
// for trivial unit-testing.
export function toLibraryMeal(meal: StoredMeal, userState: MealUserState | undefined, activity: MealCookActivity | undefined): LibraryMeal {
  return {
    mealId: meal.mealId,
    name: meal.mealName,
    description: meal.mealDescription,
    cookMins: getCookTimeMinutes(meal),
    servings: meal.servings,
    renditions: readyRenditions(meal),
    imageState: imageStateOf(meal),
    // createdAt is present on every current meal doc; the epoch fallback only guards
    // the optional type (a doc written before the field existed sorts oldest).
    createdAt: meal.createdAt ? new Date(meal.createdAt.toMillis()) : new Date(0),
    favorite: userState?.favorite ?? false,
    favoritedAt: userState?.favoritedAt ? new Date(userState.favoritedAt.toMillis()) : undefined,
    hidden: Boolean(userState?.hiddenAt),
    rating: userState?.rating ?? null,
    cookCount: activity?.cookCount ?? 0,
    lastCookedAt: activity?.lastCookedAt,
    roles: rolesOf(meal),
    componentNames: [...meal.proteins, ...meal.starches, ...meal.veggies, ...(meal.sauce ? [meal.sauce] : [])].map((c) => c.name),
    ingredientNames: meal.ingredientList.map((i) => i.name)
  }
}
